/**
 * KO-to-React Bridge
 *
 * Allows React components to be mounted inside KnockoutJS pages by wrapping them
 * as KO components. The bridge subscribes to KO observables and re-renders
 * the React component with fresh props whenever the observed data changes.
 *
 * Usage:
 *   // 1. Register a React component in index.tsx:
 *   registerReactComponent('HistoricalNcChart', HistoricalNcChart);
 *
 *   // 2. Replace the KO component from a Razor view (after react-bundle.js is loaded):
 *   WkoReact.bridgeKoComponent('historicalncchart', 'HistoricalNcChart');
 */
import React from 'react';
import { createRoot } from 'react-dom/client';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const componentRegistry = new Map();
/**
 * Registers a React component so it can be mounted via the KO bridge.
 * Call this from index.tsx for each component that should be available.
 */
export function registerReactComponent(name, component) {
    componentRegistry.set(name, component);
}
/**
 * Registers a KO component that internally mounts a React component.
 * Replaces any existing KO component with the same name.
 *
 * The KO component's `params.options` observable is subscribed to and passed
 * as the `options` prop to the React component on every change.
 *
 * @param koComponentName - The KO component name used in HTML (e.g. 'historicalncchart')
 * @param reactComponentName - The name used when calling registerReactComponent()
 */
export function registerKoComponent(koComponentName, reactComponentName) {
    const ReactComponent = componentRegistry.get(reactComponentName);
    if (!ReactComponent) {
        console.error(`[KO-React Bridge] React component '${reactComponentName}' not found in registry. ` +
            `Did you call registerReactComponent() in index.tsx?`);
        return;
    }
    const ko = window.ko;
    if (!ko) {
        console.error('[KO-React Bridge] KnockoutJS is not loaded');
        return;
    }
    if (ko.components.isRegistered(koComponentName)) {
        ko.components.unregister(koComponentName);
    }
    ko.components.register(koComponentName, {
        viewModel: {
            createViewModel: function (params, componentInfo) {
                return new KoReactBridgeViewModel(params, componentInfo.element, ReactComponent);
            },
        },
        template: '<div class="ko-react-bridge w-100 h-100"></div>',
    });
}
class KoReactBridgeViewModel {
    constructor(params, element, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ReactComponent) {
        this.ReactComponent = ReactComponent;
        this.root = null;
        this.subscription = null;
        const bridgeDiv = element.querySelector('.ko-react-bridge');
        if (!bridgeDiv) {
            // Template not yet in DOM — defer to next microtask
            setTimeout(() => this.init(params, element), 0);
            return;
        }
        this.mount(params, bridgeDiv);
    }
    init(params, element) {
        const bridgeDiv = element.querySelector('.ko-react-bridge');
        if (bridgeDiv) {
            this.mount(params, bridgeDiv);
        }
    }
    mount(params, container) {
        this.root = createRoot(container);
        const extraProps = this.extractExtraProps(params);
        if (params.options) {
            this.render(params.options(), extraProps);
            this.subscription = params.options.subscribe((newValue) => {
                this.render(newValue, extraProps);
            });
            return;
        }
        this.render({}, extraProps);
    }
    extractExtraProps(params) {
        const props = {};
        for (const [key, value] of Object.entries(params)) {
            if (key === 'options' || key === '$raw')
                continue;
            if (typeof value === 'function') {
                props[key] = value;
            }
        }
        return props;
    }
    render(options, extraProps = {}) {
        if (!this.root)
            return;
        this.root.render(React.createElement(this.ReactComponent, { options, ...extraProps }));
    }
    /** KO calls dispose() when the component is removed from the DOM. */
    dispose() {
        this.subscription?.dispose();
        if (this.root) {
            const root = this.root;
            // Defer unmount to avoid React warnings about synchronous unmount during render
            setTimeout(() => root.unmount(), 0);
            this.root = null;
        }
    }
}
